import { useEffect } from "react";

import { atom, useAtom } from "jotai";

import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import {
  MARK_NOTIFICATION_READ_MUTATION,
  NOTIFICATION_HISTORY_COUNT_QUERY,
  NOTIFICATION_HISTORY_QUERY,
  NOTIFICATION_PREFERENCES_QUERY,
  NOTIFICATIONS_QUERY,
  UNREAD_NOTIFICATIONS_COUNT_QUERY,
  UPDATE_NOTIFICATION_PREFERENCE_MUTATION,
} from "src/lib/queries/notifications";

const notificationsAtom = atom([]);
const notificationHistoryAtom = atom([]);
const notificationHistoryCountAtom = atom(0);
const notificationPreferencesAtom = atom([]);
const unreadNotificationsCountAtom = atom(0);

export const useNotifications = ({
  queryOnRender = true,
  includeNotifications = true,
  includeHistory = true,
  includeUnreadCount = true,
  includePreferences = false,
  activeLimit = 50,
  historyLimit = 5,
} = {}) => {
  const [notifications, setNotifications] = useAtom(notificationsAtom);
  const [historyNotifications, setHistoryNotifications] = useAtom(
    notificationHistoryAtom
  );
  const [historyCount, setHistoryCount] = useAtom(notificationHistoryCountAtom);
  const [preferences, setPreferences] = useAtom(notificationPreferencesAtom);
  const [unreadCount, setUnreadCount] = useAtom(unreadNotificationsCountAtom);

  const { queryApi: queryNotifications, loading: notificationsLoading } =
    useApiQuery({
      query: NOTIFICATIONS_QUERY,
      onSuccess: (data) => {
        setNotifications(data.notifications || []);
      },
    });

  const { queryApi: queryHistory, loading: historyLoading } = useApiQuery({
    query: NOTIFICATION_HISTORY_QUERY,
    onSuccess: (data) => {
      setHistoryNotifications(data.notificationHistory || []);
    },
  });

  const { queryApi: queryHistoryCount, loading: historyCountLoading } =
    useApiQuery({
      query: NOTIFICATION_HISTORY_COUNT_QUERY,
      onSuccess: (data) => {
        setHistoryCount(data.notificationHistoryCount || 0);
      },
    });

  const { queryApi: queryPreferences, loading: preferencesLoading } =
    useApiQuery({
      query: NOTIFICATION_PREFERENCES_QUERY,
      onSuccess: (data) => {
        setPreferences(data.notificationPreferences || []);
      },
    });

  const { queryApi: queryUnreadCount, loading: unreadCountLoading } =
    useApiQuery({
      query: UNREAD_NOTIFICATIONS_COUNT_QUERY,
      onSuccess: (data) => {
        setUnreadCount(data.unreadNotificationsCount || 0);
      },
    });

  const refetchNotifications = ({
    includeResolved = false,
    limit = activeLimit,
  } = {}) => queryNotifications({ includeResolved, limit });
  const refetchHistory = ({ limit = historyLimit } = {}) =>
    queryHistory({ limit });
  const refetchHistoryCount = () => queryHistoryCount();
  const refetchPreferences = () => queryPreferences();
  const refetchUnreadCount = () => queryUnreadCount();
  const refetchAll = async () => {
    const requests = [];

    if (includeNotifications) {
      requests.push(refetchNotifications());
    }

    if (includeHistory) {
      requests.push(refetchHistory());
      requests.push(refetchHistoryCount());
    }

    if (includeUnreadCount) {
      requests.push(refetchUnreadCount());
    }

    if (includePreferences) {
      requests.push(refetchPreferences());
    }

    await Promise.all(requests);
  };

  const { mutationApi: markNotificationRead, loading: markReadLoading } =
    useApiMutation({
      mutation: MARK_NOTIFICATION_READ_MUTATION,
      onSuccess: refetchAll,
    });

  const {
    mutationApi: updateNotificationPreference,
    loading: updatePreferenceLoading,
  } = useApiMutation({
    mutation: UPDATE_NOTIFICATION_PREFERENCE_MUTATION,
    onSuccess: refetchPreferences,
  });

  useEffect(() => {
    if (queryOnRender) {
      refetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryOnRender]);

  return {
    notifications,
    activeNotifications: notifications,
    historyNotifications,
    historyCount,
    preferences,
    unreadCount,
    loading:
      (includeNotifications && notificationsLoading) ||
      (includeHistory && historyLoading) ||
      (includeHistory && historyCountLoading) ||
      (includeUnreadCount && unreadCountLoading) ||
      markReadLoading ||
      updatePreferenceLoading ||
      (includePreferences && preferencesLoading),
    refetchNotifications,
    refetchHistory,
    refetchHistoryCount,
    refetchPreferences,
    refetchUnreadCount,
    refetchAll,
    markNotificationRead,
    updateNotificationPreference,
  };
};
